/**
 * Configuration du Score de santé d'un client, telle que la renvoie l'API d'administration
 * du module HealthScore (MS Reviews) : défauts du module, surcharge du client (seulement les
 * clés modifiées, fusion profonde, les tableaux remplacent) et configuration effective.
 */
export type ConfigObject = { [key: string]: unknown };

export interface HealthScoreConfig {
    defaults: ConfigObject;
    overrides: ConfigObject;
    effective: ConfigObject;
    version: number;
    updatedAt?: string | null;
    updatedBy?: string | null;
}

export interface CustomField {
    metadataType: string;
    attributeCode: string;
    points: number;
}

/** Champ custom en cours de saisie : les points restent le texte tapé jusqu'à l'envoi. */
export interface CustomFieldDraft {
    metadataType: string;
    attributeCode: string;
    points: string;
}

/** Type de métadonnée générique du client et codes d'attribut de sa définition. */
export interface AvailableCustomFields {
    metadataType: string;
    attributeCodes: string[];
}

/** Erreur de validation du module : champ (clé du ValidationProblem) et messages. */
export interface ValidationError {
    field: string;
    messages: string[];
}

export const CUSTOM_FIELDS_PATH = 'completion.customFields';
export const CUSTOM_FIELDS_MAX_POINTS_PATH = 'completion.customFieldsMaxPoints';
const DEFAULT_CUSTOM_FIELDS_MAX_POINTS = 7;

/**
 * Racines non surchargeables (refusées par le module) ou hors de l'écran : `version` du
 * fichier, poids et niveaux, règles des jours fériés.
 */
const NOT_EDITABLE_ROOTS = ['version', 'weights', 'levels', 'holidayRules'];
const NOT_EDITABLE_PATHS = [CUSTOM_FIELDS_PATH, CUSTOM_FIELDS_MAX_POINTS_PATH];

function isObject(value: unknown): value is ConfigObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getAt(source: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((node, key) => (isObject(node) ? node[key] : undefined), source);
}

/** Copie de `source` avec `value` au chemin, objets intermédiaires créés au besoin. */
export function setAt(source: ConfigObject, path: string, value: unknown): ConfigObject {
    const [key, ...rest] = path.split('.');
    if (rest.length === 0) {
        return { ...source, [key]: value };
    }
    const child = isObject(source[key]) ? source[key] as ConfigObject : {};
    return { ...source, [key]: setAt(child, rest.join('.'), value) };
}

/** Copie de `source` sans le chemin ; un objet parent devenu vide disparaît aussi. */
export function removeAt(source: ConfigObject, path: string): ConfigObject {
    const [key, ...rest] = path.split('.');
    if (!(key in source)) {
        return source;
    }
    const { [key]: removed, ...others } = source;
    if (rest.length === 0 || !isObject(removed)) {
        return others;
    }
    const child = removeAt(removed, rest.join('.'));
    return Object.keys(child).length === 0 ? others : { ...others, [key]: child };
}

/** Chemins des paramètres numériques modifiables, dans l'ordre du fichier des défauts. */
export function editableParameters(defaults: ConfigObject, prefix = ''): string[] {
    return Object.entries(defaults).flatMap(([key, value]) => {
        const path = prefix === '' ? key : `${prefix}.${key}`;
        if ((prefix === '' && NOT_EDITABLE_ROOTS.includes(key)) || NOT_EDITABLE_PATHS.includes(path)) {
            return [];
        }
        if (isObject(value)) {
            return editableParameters(value, path);
        }
        return typeof value === 'number' ? [path] : [];
    });
}

export interface ConfigDraft {
    /** Paramètre surchargé → texte saisi. Absent : valeur par défaut. */
    values: { [path: string]: string };
    /** Champs custom surchargés ; null : ceux des défauts. */
    customFields: CustomFieldDraft[] | null;
    /** Reste de la surcharge en base, que l'écran n'affiche pas : renvoyé tel quel. */
    others: ConfigObject;
}

export function toDraft(config: HealthScoreConfig): ConfigDraft {
    const paths = editableParameters(config.defaults);
    const values: { [path: string]: string } = {};
    let others = config.overrides ?? {};
    paths.forEach(path => {
        const value = getAt(others, path);
        if (value !== undefined) {
            values[path] = String(value);
            others = removeAt(others, path);
        }
    });
    const customFields = getAt(others, CUSTOM_FIELDS_PATH);
    others = removeAt(others, CUSTOM_FIELDS_PATH);
    return {
        values,
        customFields: Array.isArray(customFields)
            ? (customFields as CustomField[]).map(field => ({ ...field, points: String(field.points) }))
            : null,
        others,
    };
}

function parseNumber(text: string): number | null {
    const value = Number(text.trim().replace(',', '.'));
    return text.trim() === '' || !Number.isFinite(value) ? null : value;
}

/**
 * Surcharge à envoyer au module. `invalid` liste les champs dont le texte n'est pas un
 * nombre : rien n'est envoyé tant qu'il en reste. Les autres règles (signe, entiers,
 * fraîcheurs, total des champs custom...) sont celles du module, qui répond par champ.
 */
export function toOverrides(draft: ConfigDraft): { overrides: ConfigObject; invalid: string[] } {
    let overrides = draft.others;
    const invalid: string[] = [];
    Object.entries(draft.values).forEach(([path, text]) => {
        const value = parseNumber(text);
        if (value === null) {
            invalid.push(path);
        } else {
            overrides = setAt(overrides, path, value);
        }
    });
    if (draft.customFields !== null) {
        const fields = draft.customFields.map((field, index) => {
            const points = parseNumber(field.points);
            if (points === null) {
                invalid.push(`${CUSTOM_FIELDS_PATH}[${index}].points`);
            }
            return { metadataType: field.metadataType, attributeCode: field.attributeCode, points: points ?? 0 };
        });
        overrides = setAt(overrides, CUSTOM_FIELDS_PATH, fields);
    }
    return { overrides, invalid };
}

/** Total des points saisis, arrondi au centième (0,1 + 0,2 affiche 0,3). */
export function customFieldsTotal(fields: CustomFieldDraft[]): number {
    const total = fields.reduce((sum, field) => sum + (parseNumber(field.points) ?? 0), 0);
    return Math.round(total * 100) / 100;
}

export function customFieldsMaxPoints(config: HealthScoreConfig): number {
    const max = getAt(config.effective, CUSTOM_FIELDS_MAX_POINTS_PATH) ?? getAt(config.defaults, CUSTOM_FIELDS_MAX_POINTS_PATH);
    return typeof max === 'number' ? max : DEFAULT_CUSTOM_FIELDS_MAX_POINTS;
}

/**
 * Erreurs d'un ValidationProblem (`errors` : champ → messages). Une réponse sans `errors`
 * donne une seule erreur générale avec son `detail` ou son `title`.
 */
export function validationErrors(problem: unknown): ValidationError[] {
    if (!isObject(problem)) {
        return [];
    }
    if (isObject(problem.errors)) {
        return Object.entries(problem.errors).map(([field, messages]) => ({
            field,
            messages: Array.isArray(messages) ? messages.map(String) : [String(messages)],
        }));
    }
    const message = problem.detail ?? problem.title;
    return typeof message === 'string' ? [{ field: '', messages: [message] }] : [];
}

/** Paramètres d'une fraîcheur : le module range l'erreur « fullDays ≥ zeroDays » sous leur objet parent. */
const SHAPE_KEYS = ['fullDays', 'zeroDays'];

/**
 * Erreurs du module pour un paramètre. Les clés du ValidationProblem sont les chemins JSON de la
 * surcharge (`completion.photos.target`, `completion.customFields[0].points`) ; l'erreur de forme
 * d'une fraîcheur (`ereputation.responseDelay`) s'affiche sur ses deux bornes.
 */
export function errorsFor(errors: ValidationError[], path: string): string[] {
    const segments = path.split('.');
    const shapeParent = SHAPE_KEYS.includes(segments[segments.length - 1]) ? segments.slice(0, -1).join('.') : null;
    return errors
        .filter(error => error.field === path || error.field.startsWith(`${path}[`) || error.field === shapeParent)
        .flatMap(error => error.messages);
}
